/**
 * @fileoverview React hook for consistent compact money formatting across dashboard components.
 * @module shared/hooks/useCompactMoney
 */

import { useCallback } from 'react'
import { compactMoney as compactMoneyCore } from '@/shared/utils/money'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { i18next } from '@/lib/i18n'

/**
 * Hook returning a formatter for compact monetary values.
 *
 * This centralises the logic so all dashboard UIs (metric cards, charts, tooltips)
 * format currency in the same way (e.g. `254.0K zł`).
 *
 * @returns Formatter function accepting an amount and optional max fraction digits.
 * @example
 * ```ts
 * const compactMoney = useCompactMoney()
 * const label = compactMoney(254000) // "254.0K zł"
 * ```
 */
export function useCompactMoney(): (amount: number, maximumFractionDigits?: number) => string {
  const { currency } = useReportingCurrency()
  const locale = i18next.language

  const format = useCallback(
    (amount: number, maximumFractionDigits = 1): string => {
      if (!currency) {
        // Fallback: integer-only formatting when reporting currency is not configured.
        return `${Math.trunc(amount).toLocaleString(locale)}`
      }

      return compactMoneyCore(amount, currency, locale, maximumFractionDigits)
    },
    [currency, locale]
  )

  return format
}
